//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { fullScreenWorkflowActions } from '@galleries/actions/fullScreenWorkflowActions';
import { REQUEST_RIGHTS_MODAL_NON_MENTIONED_ACTIONS } from '@galleries/constants/galleriesConstants';
import {
  FULLSCREEN_WORKFLOW_FLAGS,
  FULLSCREEN_WORKFLOW_NAMES,
} from '@galleries/models/fullScreenWorkflowModel';
import { galleriesDataActions } from '@galleries/reducers/galleriesDataSlice';
import { requestRightsActions } from '@legacy-components/rights-management/request-rights-actions';
import { ICuralateStore } from '@models/AppState';
import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import ItemId from '@models/ItemId';
import PartialCuid from '@models/PartialCuid';
import {
  getBulkActionsActions,
  getUniversalImageActionsOauthService,
} from '@react-bridge/wrappers/ng-service-provider';
import { createAction } from '@utils/actions';
import { copyTextToClipboard } from '@utils/copyToClipboard';
import { ReduxDispatch } from 'app/curalate.store';
import { personalizeRightsRequestMessage } from '@legacy-components/rights-management/request-rights-util';
import { getIsIncludeUsernameEnabled } from '@galleries/selectors/bulk-rights-selectors/getRightsPersonalizationFeatures';
import { RightsRequestMessageSource } from '@apis/rights-management/rightsManagementApiModels';
import CommunicationType from '@models/RightsCommunicationType';
import { SocialNetwork } from '@models/SocialNetwork';
import { GENERAL_MESSAGING_TIKTOK } from '@account-settings/components/rights-requests/constants/rightRequestConstants';

const openModal = (materializedItems: IContentLibraryItem[]) => {
  return createAction({
    type: REQUEST_RIGHTS_MODAL_NON_MENTIONED_ACTIONS.OPEN_MODAL,
    materializedItems,
  });
};

const closeModal = () => {
  return createAction({
    type: REQUEST_RIGHTS_MODAL_NON_MENTIONED_ACTIONS.CLOSE_MODAL,
  });
};

const setSelectedIndex = (selectedIndex: number) => {
  return createAction({
    type: REQUEST_RIGHTS_MODAL_NON_MENTIONED_ACTIONS.SET_SELECTED_INDEX,
    selectedIndex,
  });
};

const requestRights = (itemId: ItemId) => {
  return createAction({
    type: REQUEST_RIGHTS_MODAL_NON_MENTIONED_ACTIONS.REQUEST_RIGHTS,
    itemId,
  });
};

const receiveRightsSucceeded = (itemId: ItemId) => {
  return createAction({
    type: REQUEST_RIGHTS_MODAL_NON_MENTIONED_ACTIONS.REQUEST_RIGHTS_SUCCEEDED,
    itemId,
  });
};

const receiveRightsFailed = (itemId: ItemId) => {
  return createAction({
    type: REQUEST_RIGHTS_MODAL_NON_MENTIONED_ACTIONS.REQUEST_RIGHTS_FAILED,
    itemId,
  });
};

const setSelectedIndexWithinBounds =
  (selectedIndex: number) =>
  (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
    const numImages =
      getState().galleries.requestRightsNonMentionedModal.materializedItems
        .length;

    if (selectedIndex >= numImages) {
      dispatch(setSelectedIndex(0));
    } else if (selectedIndex < 0) {
      dispatch(setSelectedIndex(numImages - 1));
    } else {
      dispatch(setSelectedIndex(selectedIndex));
    }
  };

export type RequestRightsNonMentionedModalAction = ReturnType<
  | typeof openModal
  | typeof closeModal
  | typeof setSelectedIndex
  | typeof requestRights
  | typeof receiveRightsSucceeded
  | typeof receiveRightsFailed
>;

export const requestRightsNonMentionedModalActions = {
  setSelectedIndexWithinBounds,

  closeModal: () => (dispatch: ReduxDispatch) => {
    dispatch(closeModal());
    dispatch(
      fullScreenWorkflowActions.stopWorkflow(
        FULLSCREEN_WORKFLOW_NAMES.REQUEST_RIGHTS_NON_AT_MENTIONED,
      ),
    );
  },

  openModal:
    (materializedItems: IContentLibraryItem[]) => (dispatch: ReduxDispatch) => {
      dispatch(openModal(materializedItems));
      dispatch(
        fullScreenWorkflowActions.startWorkflow(
          FULLSCREEN_WORKFLOW_NAMES.REQUEST_RIGHTS_NON_AT_MENTIONED,
        ),
      );
    },

  requestRightsWithOauthCheck:
    (contentItem: IContentLibraryItem) =>
    async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const partialUID = PartialCuid.build(contentItem);
      const itemId = contentItem.id;

      const requestRightsState = getState().requestRights;

      // TODO: PD-249308 - remove rights_requests_include_username flag
      const isIncludeUsernameEnabled = getIsIncludeUsernameEnabled(getState());

      const isTikTokItem =
        contentItem?.socialDetails?.socialNetwork === SocialNetwork.TikTok;

      let selectedConfigurationId = requestRightsState.selectedConfigurationId;

      if (isTikTokItem) {
        const tikTokConfigEntry = Object.entries(
          requestRightsState.configurations.byId,
        ).find(
          ([, config]) =>
            config?.name?.toLowerCase() ===
            GENERAL_MESSAGING_TIKTOK.toLowerCase(),
        );

        if (tikTokConfigEntry) {
          selectedConfigurationId = parseInt(tikTokConfigEntry[0], 10);
        }
      }

      const selectedConfiguration =
        requestRightsState.configurations.byId[selectedConfigurationId];

      const selectedMessage =
        selectedConfiguration.rightsRequestData.data[
          requestRightsState.selectedMessageIndex
        ];
      const personalizedMessage = {
        ...selectedMessage,
        message: personalizeRightsRequestMessage(
          isIncludeUsernameEnabled,
          selectedMessage.message,
          contentItem.socialDetails?.user?.username ?? '',
        ),
        communicationType:
          selectedMessage?.communicationType ?? CommunicationType('COMMENT'),
      };
      const numMessages = selectedConfiguration.rightsRequestData.data.length;

      const modalState = getState().galleries.requestRightsNonMentionedModal;
      const numItemsPending = Object.keys(modalState.rightsPending).length;
      const numNonMentionedItems = modalState.materializedItems.length;

      const clientId = getState().session.client.id;
      const cuid = Cuid.build(contentItem, ClientId.fromString(clientId));

      const BulkActionsActions = getBulkActionsActions();

      await copyTextToClipboard(personalizedMessage.message);

      dispatch(requestRights(itemId));

      const { rightsInitiated, close } =
        getUniversalImageActionsOauthService().requestRightsWithOAuthCheck({
          targetUrl: contentItem.socialDetails.link,
          approvalPhrase: personalizedMessage.approvalPhrase,
          requestMessage: personalizedMessage.message,
          communicationType: personalizedMessage.communicationType,
          requestMessageSource: RightsRequestMessageSource.ConfiguredMessage,
          requestMessageLanguage: selectedMessage?.languageCode,
          cuid,
        });

      rightsInitiated
        .then((initiated) => {
          if (initiated) {
            dispatch(receiveRightsSucceeded(itemId));
            dispatch(
              BulkActionsActions.updateItemOverlayDetails(partialUID, true),
            );
            dispatch(galleriesDataActions.getGalleryItemByCuid(cuid));
            dispatch(BulkActionsActions.clearSelectedItems());
            dispatch(
              fullScreenWorkflowActions.setFlag(
                FULLSCREEN_WORKFLOW_FLAGS.RIGHTS_REQUESTED,
                true,
              ),
            );

            if (numItemsPending + 1 < numNonMentionedItems) {
              // If there are more images, select the next one and rotate to the next available rights message
              dispatch(
                setSelectedIndexWithinBounds(modalState.selectedIndex + 1),
              );
              dispatch(
                requestRightsActions.selectMessageIndex(
                  (requestRightsState.selectedMessageIndex + 1) % numMessages,
                ),
              );
            }
          } else {
            dispatch(receiveRightsFailed(itemId));
          }
        })
        .catch(() => {
          dispatch(receiveRightsFailed(itemId));
        })
        .finally(() => {
          close();
        });
    },
};
